import { TFunction } from 'i18next';
import { Dispatch, SetStateAction } from 'react';

import { Address } from '../../../../types/address';
import { noColumnStyle } from '../../../../utils/constants/tables';
import { dateCell } from '../../../../utils/helpers/dataTable';
import ActionsTableMenu from '../../Components/ActionsTableMenu/ActionsTableMenu';

export const tableStorageKey = 'incompleteOrders_dataTable';

export type IncompleteOrders = {
  id: number;
  orderId: number;
  orderSerialNumber: string;
  phoneNumber: string;
  rawAddress: string;
  resolvedAddress: Address;
  address: Address;
  status: string;
  modifiedAt: string;
};

export function getColumnHeadersMap(t: TFunction) {
  return {
    no: t('No.'),
    orderSerialNumber: t('Serial Number'),
    ordererName: t('Orderer'),
    senderName: t('Sender'),
    recipientName: t('Recipient'),
    createdAt: t('Created Date'),
    phoneNumber: t('Phone'),
    rawAddress: t('Address'),
    addressType: t('Address type'),
    status: t('Status'),
  };
}

export enum IncompleteOrderStatus {
  CREATED = 'CREATED',
  NOTIFICATION_SENT = 'NOTIFICATION_SENT',
  NOTIFICATION_DUPLICATE = 'NOTIFICATION_DUPLICATE',
  AUTO_RESOLVED = 'AUTO_RESOLVED',
  RESOLVED_BY_ORDERER = 'RESOLVED_BY_ORDERER',
  RESOLVED_BY_SENDER = 'RESOLVED_BY_SENDER',
  RESOLVED_BY_RECIPIENT = 'RESOLVED_BY_RECIPIENT',
  RESOLVED_BY_EMPLOYEE = 'RESOLVED_BY_EMPLOYEE',
  AUTO_RESOLVED_FROM_PAST_SHIPMENT = 'AUTO_RESOLVED_FROM_PAST_SHIPMENT',
}

export const incompleteOrderStatusOptions = [
  { label: 'Created', value: IncompleteOrderStatus.CREATED },
  {
    label: 'Notification sent',
    value: IncompleteOrderStatus.NOTIFICATION_SENT,
  },
  {
    label: 'Notification duplicate',
    value: IncompleteOrderStatus.NOTIFICATION_DUPLICATE,
  },
  { label: 'Auto resolved', value: IncompleteOrderStatus.AUTO_RESOLVED },
  {
    label: 'Resolved by orderer',
    value: IncompleteOrderStatus.RESOLVED_BY_ORDERER,
  },
  {
    label: 'Resolved by sender',
    value: IncompleteOrderStatus.RESOLVED_BY_SENDER,
  },
  {
    label: 'Resolved by recipient',
    value: IncompleteOrderStatus.RESOLVED_BY_RECIPIENT,
  },
  {
    label: 'Resolved by employee',
    value: IncompleteOrderStatus.RESOLVED_BY_EMPLOYEE,
  },
  {
    label: 'Auto resolved from past shipment',
    value: IncompleteOrderStatus.AUTO_RESOLVED_FROM_PAST_SHIPMENT,
  },
];

export const editableStatuses = [
  'CREATED',
  'NOTIFICATION_SENT',
  'NOTIFICATION_DUPLICATE',
];

export function additionalColumnProperties(
  t: TFunction,
  column:
    | keyof ReturnType<typeof getColumnHeadersMap>
    | 'time_from_pickup'
    | 'actions',
  setContextMenuSelection: Dispatch<SetStateAction<any>>,
  setAction: (t: string) => void,
  setCaller: Dispatch<SetStateAction<'context-menu' | 'group-actions'>>,
  readGuard: boolean,
  editGuard: boolean
) {
  switch (column) {
    case 'no':
      return {
        noColumnStyle,
        style: {
          width: 30,
          maxWidth: 30,
        },
        bodyStyle: {
          width: 30,
          maxWidth: 30,
          display: 'flex',
          justifyContent: 'center',
        },
      };

    case 'createdAt':
      return {
        body: (row: any) => dateCell(row[column] ?? ''),
        style: {
          width: 180,
        },
        bodyStyle: {
          width: 180,
        },
      };

    case 'orderSerialNumber':
    case 'ordererName':
    case 'senderName':
    case 'recipientName':
    case 'phoneNumber':
    case 'rawAddress':
    case 'status':
    case 'addressType':
      return {
        style: {
          width: 200,
        },
        bodyStyle: {
          width: 200,
        },
      };

    case 'actions':
      return {
        body: (row: IncompleteOrders) => {
          return (
            <ActionsTableMenu
              actions={[
                {
                  label: t('View Details'),
                  icon: 'fas fa-search',
                  disabled: !readGuard,
                  command: () => {
                    setContextMenuSelection(row);
                    setAction('view-details');
                    setCaller('context-menu');
                  },
                },
                {
                  label: t('Edit'),
                  icon: 'fas fa-edit',
                  disabled:
                    !editGuard || !editableStatuses.includes(row.status),
                  command: () => {
                    setContextMenuSelection(row);
                    setAction('edit');
                    setCaller('context-menu');
                  },
                },
              ]}
            />
          );
        },
        style: {
          width: 60,
          borderLeft: '1px solid #e9ecef',
          flex: '0 1 auto',
          position: 'sticky',
        },
        bodyStyle: {
          width: 60,
          borderLeft: '1px solid #e9ecef',
          flex: '0 1 auto',
          position: 'sticky',
          justifyContent: 'center',
        },
      };

    default:
      return {};
  }
}
